import path from 'path';

import { TopologyService } from '~services';
import { decapitalizeFirstCharacter } from '~utils/stringUtils';

import LockStateEnum from '../../devices/enums/lockStateEnum';
import Heartbeat, { SharedHeartbeat } from '../interfaces/heartbeat';

class ItemHeartbeatsService extends TopologyService {
	protected readonly path = 'items';

	public readonly itemsSubpath = 'iteminstances';
	public readonly latestHeartbeatSubPath = 'heartbeats/latest';

	constructor() {
		super('v1');
	}

	async getLatestHeartbeat(itemId: string, itemGroupId: string): Promise<SharedHeartbeat> {
		const { data } = await this._client.get<SharedHeartbeatResponse>(
			path.join(this.path, itemGroupId, this.itemsSubpath, itemId, this.latestHeartbeatSubPath)
		);

		return ItemHeartbeatsService.fromResponse(data);
	}

	static fromResponse(data: SharedHeartbeatResponse): SharedHeartbeat {
		const {
			lastUpdated,
			ignition,
			immobiliserStateEnabled,
			doors,
			lockState,
			batteryLevel,
			batteryLevelEnum,
			batteryCharging,
			location,
			latitude,
			longitude,
			...rest
		} = data;

		return {
			...rest,
			immobiliserEnabled: immobiliserStateEnabled,
			doorsState: doors?.map((el) => ({
				...el,
				location: decapitalizeFirstCharacter(el.location),
			})),
			ignitionDisabled: ignition != null ? ignition.toLowerCase() === 'disabled' : undefined,
			dateUpdated: lastUpdated ? new Date(lastUpdated) : undefined,
			lockState: lockState != null ? (lockState as LockStateEnum) : undefined,
			deviceBatteryLevel: batteryLevel,
			deviceBatteryLevelEnum: batteryLevelEnum,
			batteryIsCharging: batteryCharging,
			location:
				location ??
				(latitude != null && longitude != null ?
					{ latitude: latitude, longitude: longitude }
				:	undefined),
		};
	}
}

interface BaseHeartbeatResponse {
	// Unsure, we should have an unique per heartbeat, but there is
	// not
	id?: string;
	deviceId: string;
	lastUpdated: string;
	batteryLevel: number; // the device battery level
	batteryLevelEnum: string;
	range: number;
	locationType: string;
}

type SharedHeartbeatResponse = Partial<CarHeartbeatResponse> &
	Partial<GenericLockHeartbeatResponse> &
	Partial<BikeLockHeartbeatResponse> &
	BaseHeartbeatResponse;

interface CarHeartbeatResponse extends BaseHeartbeatResponse {
	cell: object;
	location: {
		accuracy: number;
		latitude: number;
		longitude: number;
	};
	mileage: number;
	lockState: 'locked' | 'unlocked' | 'secured';
	doors: {
		location: 'Driver' | 'Passenger' | 'DriverRear' | 'PassengerRear';
		open: boolean;
	}[];
	vehicleFuelVolume?: number;
	vehicleBatteryLevel?: number;
	immobiliserStateEnabled: boolean;
	ignition: string;
	batteryCharging: boolean;
	keyDetected: boolean;
	rangeElectric?: number;
}

// Shared by doorlock, trailerlock, batterybackup, cpac
interface GenericLockHeartbeatResponse extends BaseHeartbeatResponse {
	firmwareVersion: string;
	latitude: number;
	longitude: number;
}

interface BikeLockHeartbeatResponse extends BaseHeartbeatResponse {
	firmwareVersion: string;
	temperature: number;
	state: string;
	type: string;
	cycles: number;
	borderRouterId: string;
	eventCode: number;
	communicationType: string[];
	mileage: number;
	lockState: number;
	immobiliserStateEnabled: boolean;
}

export default ItemHeartbeatsService;
